import { useSelector } from 'react-redux'
import { FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import {
  ErrorTooltip,
  PermissionArea,
  PuiTextField,
} from '@pbt/pbt-ui-components'
import type { Field } from '@pbt/pbt-ui-components/src/types/validation'

import {
  AdministrationCourseType,
  AdministrationCourseTypeStr,
} from '~/constants/prescription'
import { getCRUDByArea } from '~/store/reducers/auth'
import type { DataHandle } from '~/types'
import { handleNumberInput } from '~/utils'

const useStyles = makeStyles(
  (theme) => ({
    radioLabel: {
      marginRight: theme.spacing(3),
    },
    disabledRadioInput: {
      pointerEvents: 'none',
      cursor: 'pointer',
    },
    customLengthInput: {
      marginTop: -20,
      maxWidth: 85,
    },
    text: {
      fontSize: '1.6rem',
    },
  }),
  { name: 'LengthOfCourseSection' },
)

export interface LengthOfCourseSectionProps {
  administrationCourseLength: Field
  administrationCourseType: Field
  isRequired?: boolean
}

export interface LengthOfCourseSectionHandle
  extends DataHandle<{
    administrationCourseLength: number
    administrationCourseType: string
  }> {}

const LengthOfCourseSection = ({
  isRequired = false,
  administrationCourseLength,
  administrationCourseType,
}: LengthOfCourseSectionProps) => {
  const classes = useStyles()

  const permissions = useSelector(getCRUDByArea(PermissionArea.PRESCRIPTION))

  const isCustomCourse =
    administrationCourseType.value === AdministrationCourseType.CUSTOM

  return (
    <Grid container>
      <ErrorTooltip
        disablePortal
        message={administrationCourseType.message}
        open={administrationCourseType.open && !administrationCourseType.valid}
        validateTag={administrationCourseType.validateTag}
      >
        <RadioGroup
          row
          value={administrationCourseType.value}
          onChange={(event, value) => administrationCourseType.setValue(value)}
        >
          <FormControlLabel
            className={classes.radioLabel}
            control={<Radio disableRipple color="secondary" />}
            label={
              <PuiTextField
                className={classes.customLengthInput}
                classes={{
                  root: classNames({
                    [classes.disabledRadioInput]: !isCustomCourse,
                  }),
                }}
                disabled={!permissions.update || !isCustomCourse}
                field={{
                  ...administrationCourseLength,
                  set: handleNumberInput(
                    administrationCourseLength.setValue,
                    10,
                    0,
                  ),
                }}
                inputProps={{ maxLength: 10 }}
                label={`${administrationCourseLength.label}${
                  isRequired && isCustomCourse ? '*' : ''
                }`}
                margin="none"
              />
            }
            value={AdministrationCourseType.CUSTOM}
          />
          <FormControlLabel
            className={classes.radioLabel}
            classes={{ label: classes.text }}
            control={<Radio disableRipple color="secondary" />}
            disabled={!permissions.update}
            label={AdministrationCourseTypeStr.UNTIL_GONE}
            value={AdministrationCourseType.UNTIL_GONE}
          />
          <FormControlLabel
            className={classes.radioLabel}
            classes={{ label: classes.text }}
            control={<Radio disableRipple color="secondary" />}
            disabled={!permissions.update}
            label={AdministrationCourseTypeStr.AS_NEEDED}
            value={AdministrationCourseType.AS_NEEDED}
          />
        </RadioGroup>
      </ErrorTooltip>
    </Grid>
  )
}

export default LengthOfCourseSection
